import {
  getPreferredIdForStop,
  Stop,
  StopContext,
} from "@vygruppen/vy-train-map";
import {
  EventGroup,
  GenericType,
  OperationalInformationTypes,
} from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { z } from "zod";

export const isCustomEmptyType = (inputType: string | undefined | null) =>
  inputType === "" || inputType === "NO_REASON";

export const isCustomInputType = (inputType: string | undefined | null) =>
  inputType === "CUSTOM" ||
  inputType === "TRAIN_GENERAL" ||
  inputType === "STATION_GENERAL" ||
  inputType === "INFRASTRUCTURE_GENERAL";

export const descriptionErrorMessage = "Du må fylle ut en beskrivelse";

export type DescribedType = z.infer<typeof describedTypeSchema>;

// CustomTexts without title
export const distributionTexts = z.object({
  NOB: z.string().min(1, { message: descriptionErrorMessage }).nullish(),
  SWE: z.string().min(1, { message: descriptionErrorMessage }).nullish(),
  ENG: z.string().min(1, { message: descriptionErrorMessage }).nullish(),
});

export const distributionInput = z.object({
  contextTypes: z.array(z.string()).min(1),
  texts: distributionTexts,
});

export const describedTypeSchema = z.object({
  type: z.string().nullable(),
  description: distributionTexts.optional(),
});

export const dropsLogTextInput = z.object({
  enabled: z.boolean(),
  texts: z.object({
    NOB: z.string(),
  }),
});

/*
 * Filter out irrelevant or non-supported reasons.
 * Sort the remaining reasons alphabetically.
 * */
function sortAndFilterReasons(
  reasons: GenericType[],
  filterTag: "TRAIN" | "INFRASTRUCTURE",
) {
  return reasons
    .filter((r) => r.type !== "OTHER" && r.tags.includes(filterTag))
    .sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
}

const plannedEventActions = [
  "PLANNED_ALTRANS_NOT_UPDATED_LONG_DISTANCE",
  "PLANNED_ALTRANS_NOT_UPDATED_LOCAL",
  "PLANNED_ALTRANS_UPDATED",
];

const filterActions = (actions: GenericType[], isPlanned: boolean = false) =>
  actions.filter((action) =>
    isPlanned
      ? plannedEventActions.includes(action.type) ||
        isCustomInputType(action.type)
      : !plannedEventActions.includes(action.type),
  );

export const missingStationErrorMessage =
  "Du må velge en stasjon for å opprette hendelsen";

export const filterOpInfoTypesTrain = (
  opInfoTypes: OperationalInformationTypes,
) => ({
  events: opInfoTypes.events.filter(
    (event) => event.group === EventGroup.TRAIN,
  ),
  reasons: sortAndFilterReasons(opInfoTypes.reasons, "TRAIN"),
  actions: filterActions(opInfoTypes.actions),
  prognoses: opInfoTypes.prognoses.filter((prognosis) =>
    prognosis.tags.includes("TRAIN"),
  ),
});

export const filterOpInfoTypesInfrastructure = (
  opInfoTypes: OperationalInformationTypes,
  isPlanned: boolean,
) => ({
  events: opInfoTypes.events.filter(
    (event) => event.group === EventGroup.INFRASTRUCTURE,
  ),
  reasons: sortAndFilterReasons(opInfoTypes.reasons, "INFRASTRUCTURE"),
  consequences: opInfoTypes.consequences,
  actions: filterActions(opInfoTypes.actions, isPlanned),
  prognoses: opInfoTypes.prognoses.filter((prognosis) =>
    prognosis.tags.includes("INFRASTRUCTURE"),
  ),
});

export const stopIdSchema = z.object(
  {
    id: z.string().min(1),
    name: z.string().nullish(),
    context: z.nativeEnum(StopContext),
  },
  {
    error: missingStationErrorMessage,
  },
);

export const stopSchema = z.object({
  name: z.string(),
  countryCode: z.enum(["NO", "SE"]),
  stopIds: stopIdSchema.array().min(1),
});

export type StopId = z.infer<typeof stopIdSchema>;

export const mapStopToStopIdSchema = (stop: Stop) => {
  const preferred = getPreferredIdForStop(stop) ?? stop.stopIds[0];
  return {
    id: preferred.id,
    name: preferred.name ?? stop.name ?? null,
    context: preferred.context,
  };
};
